import { useRef, useCallback, useEffect } from "react";
import {
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";

/**
 * Custom hook for managing @dnd-kit drag-and-drop for sets
 * Handles scroll prevention, keyboard dismissal, and sensor configuration
 * Supports both touch (mobile) and mouse (desktop) input
 */
export const useSetDragKit = () => {
  // Track scroll prevention state
  const scrollPreventionRef = useRef(null);

  // Configure sensors for both touch and mouse
  // TouchSensor: delay allows scrolling before drag starts (mobile)
  // MouseSensor: immediate activation for better desktop UX
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250, // milliseconds to hold before drag starts (allows scrolling)
      tolerance: 5, // pixels of motion allowed during delay
    },
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8, // pixels to move before drag starts (prevents accidental drags)
    },
  });

  // Use both sensors - dnd-kit will automatically choose the right one
  const sensors = useSensors(touchSensor, mouseSensor);

  // Cleanup scroll prevention on unmount
  useEffect(() => {
    return () => {
      // Restore scrolling if component unmounts during drag
      if (scrollPreventionRef.current) {
        scrollPreventionRef.current.forEach(
          ({ element, originalTouchAction, originalOverflow }) => {
            if (element) {
              element.style.touchAction = originalTouchAction || "";
              element.style.overflow = originalOverflow || "";
            }
          }
        );
        scrollPreventionRef.current = null;
      }
    };
  }, []);

  /**
   * Handler for drag start - dismisses keyboard and prevents scrolling
   */
  const handleDragStart = useCallback(() => {
    // Dismiss keyboard when drag starts to prevent interference with touch events
    if (typeof document !== "undefined") {
      const activeElement = document.activeElement;
      // Blur any focused input/textarea to dismiss keyboard
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true")
      ) {
        activeElement.blur();
      }

      // Prevent scrolling on all scroll containers during drag
      // Find all scrollable elements and disable touch scrolling
      // Initialize array if needed
      if (!scrollPreventionRef.current) {
        scrollPreventionRef.current = [];
      }

      // Find React Native Web ScrollView components (they use data-testid or specific classes)
      const scrollViews = document.querySelectorAll(
        "[data-testid*='scroll'], [class*='ScrollView'], div[style*='overflow'], div[style*='Overflow'], [data-scroll-container]"
      );

      scrollViews.forEach((container) => {
        const htmlElement = container;
        if (htmlElement && htmlElement.style) {
          // Store original style
          const originalTouchAction = htmlElement.style.touchAction;
          const originalOverflow = htmlElement.style.overflow;
          const originalOverflowY = htmlElement.style.overflowY;
          const originalOverflowX = htmlElement.style.overflowX;

          // Prevent scrolling
          htmlElement.style.touchAction = "none";
          htmlElement.style.overflow = "hidden";
          htmlElement.style.overflowY = "hidden";
          htmlElement.style.overflowX = "hidden";

          // Store for restoration
          scrollPreventionRef.current.push({
            element: htmlElement,
            originalTouchAction,
            originalOverflow,
            originalOverflowY,
            originalOverflowX,
          });
        }
      });

      // Also prevent scrolling on body/html
      const body = document.body;
      const html = document.documentElement;
      if (body && body.style) {
        const bodyOriginalTouchAction = body.style.touchAction;
        const bodyOriginalOverflow = body.style.overflow;
        body.style.touchAction = "none";
        body.style.overflow = "hidden";
        scrollPreventionRef.current.push({
          element: body,
          originalTouchAction: bodyOriginalTouchAction,
          originalOverflow: bodyOriginalOverflow,
          originalOverflowY: body.style.overflowY,
          originalOverflowX: body.style.overflowX,
        });
      }
      if (html && html.style) {
        const htmlOriginalTouchAction = html.style.touchAction;
        const htmlOriginalOverflow = html.style.overflow;
        html.style.touchAction = "none";
        html.style.overflow = "hidden";
        scrollPreventionRef.current.push({
          element: html,
          originalTouchAction: htmlOriginalTouchAction,
          originalOverflow: htmlOriginalOverflow,
          originalOverflowY: html.style.overflowY,
          originalOverflowX: html.style.overflowX,
        });
      }
    }
  }, []);

  /**
   * Handler for drag end - restores scrolling and handles move logic
   * @param {Function} onMoveSet - Callback to handle the actual set move
   * @param {Array} displaySets - Array of sets to find indices from
   */
  const handleDragEnd = useCallback(
    (onMoveSet, displaySets) => {
      return (event) => {
        // Handle the actual move FIRST (before restoring scroll)
        // This ensures the item appears in its final position immediately
        const { active, over } = event;
        if (over && active.id !== over.id) {
          const dragIndex = displaySets.findIndex((s) => s?.id === active.id);
          const hoverIndex = displaySets.findIndex((s) => s?.id === over.id);

          if (dragIndex !== -1 && hoverIndex !== -1) {
            // Execute move immediately for instant visual feedback
            onMoveSet(dragIndex, hoverIndex);
          }
        }

        // Restore scrolling after move is processed
        // Use requestAnimationFrame to ensure DOM updates happen first
        requestAnimationFrame(() => {
          if (scrollPreventionRef.current) {
            scrollPreventionRef.current.forEach(
              ({
                element,
                originalTouchAction,
                originalOverflow,
                originalOverflowY,
                originalOverflowX,
              }) => {
                if (element && element.style) {
                  element.style.touchAction = originalTouchAction || "";
                  element.style.overflow = originalOverflow || "";
                  if (originalOverflowY !== undefined) {
                    element.style.overflowY = originalOverflowY || "";
                  }
                  if (originalOverflowX !== undefined) {
                    element.style.overflowX = originalOverflowX || "";
                  }
                }
              }
            );
            scrollPreventionRef.current = null;
          }
        });
      };
    },
    []
  );

  return {
    sensors,
    handleDragStart,
    handleDragEnd,
  };
};
